import { call, put, takeLatest, takeEvery } from 'redux-saga/effects';

import { RequestService } from '@tcc-online/request-service';

import { UserService } from '../../services/common/UserService';
import AuthConstants, {
    LOGIN,
    SELECT_CUSTOMER,
    LOGOUT,
    REFRESH_ACCESS_FAIL,
    REFRESH_ACCESS,
} from '../../constants/actions/auth.constants';
import { IAction, IAuthTokens } from '../../types';
import { AuthActions } from '../../actions';
import { NotificationActions } from '../../common/actions/NotificationActions';
import SagaHelpers from '../../common/helpers/SagaHelpers';
import {
    GET_ACTIVE_QUESTIONNAIRES,
    GET_ACTIVE_QUESTIONNAIRES_FAIL,
    GET_ACTIVE_QUESTIONNAIRES_SUCCESS,
} from '../../constants/actions/users.constants';
import DemographicActions from '../../common/actions/DemographicActions';
import { SIGN_IN_PATH } from '../../constants/route.constants';
import NotificationService from '../../common/services/NotificationService';
import { NOTIFICATION_TYPE_ERROR } from '../../constants/common/notifications.constants';

export interface ILoginAction extends IAction {
    type: string;
    payload: { username: string; password: string };
}

export function* login(action: ILoginAction, timestamp?: number) {
    yield baseLogin(UserService.authUser, action.payload, LOGIN, timestamp);
}

export interface ISelectCustomerAction extends IAction {
    type: string;
    payload: { accessToken: string; refreshToken: string };
}

export function* selectCustomer({ payload }: ISelectCustomerAction) {
    yield call(
        UserService.setCustomer,
        payload.accessToken,
        payload.refreshToken,
    );
}

export function* getActiveQuestionnaires() {
    if (!UserService.isLogged()) {
        return;
    }

    yield SagaHelpers.handleRequest({
        request: [UserService.getActiveQuestionnaires, null],
        actions: [
            (payload) => ({
                type: GET_ACTIVE_QUESTIONNAIRES_SUCCESS,
                payload,
            }),
            (error) => ({ type: GET_ACTIVE_QUESTIONNAIRES_FAIL, error }),
        ],
    });
}

export function* logout() {
    yield call(UserService.logout);
}

class RefreshError extends Error {
    public status;
    constructor(message: string, status) {
        super(message);
        this.status = status;
    }
}

export function* refreshAccess(_, timestamp?: number) {
    const errorMessage = 'fe.warning.session-expired';
    try {
        const tokens = yield call([
            RequestService,
            RequestService.refreshTokens,
        ]);

        if (!tokens) {
            throw new RefreshError(errorMessage, 401);
        }
    } catch (err) {
        const { message, status } = err as {
            message?: string;
            status?: number;
        };
        if (status === 401) {
            yield put(AuthActions.logout());
        }
        yield put(NotificationActions.error(message ?? '', timestamp));
        yield put({ type: REFRESH_ACCESS_FAIL, error: message });
    }
}

function* resetPassword(action) {
    yield SagaHelpers.handleRequest({
        request: [UserService.resetPassword, action.payload],
        actions: [
            (payload) => ({
                type: AuthConstants.RESET_PASSWORD_SUCCESS,
                payload,
            }),
            (error) => ({ type: AuthConstants.RESET_PASSWORD_FAIL, error }),
        ],
    });
}

function* requestResetPassword(action) {
    yield SagaHelpers.handleRequest({
        request: [UserService.requestResetPassword, action.payload],
        actions: [
            (payload) => ({
                type: AuthConstants.REQUEST_RESET_PASSWORD_SUCCESS,
                payload,
            }),
            (error) => ({
                type: AuthConstants.REQUEST_RESET_PASSWORD_FAIL,
                error,
            }),
        ],
    });
}

function* changePassword(action) {
    yield SagaHelpers.handleRequest({
        request: [UserService.changePassword, action.payload],
        actions: [
            (payload) => ({
                type: AuthConstants.CHANGE_PASSWORD_SUCCESS,
                payload,
            }),
            (error) => ({ type: AuthConstants.CHANGE_PASSWORD_FAIL, error }),
        ],
    });
}

export function* loginWithToken(action: ILoginAction, timestamp?: number) {
    yield baseLogin(
        UserService.authUserWithToken,
        action.payload,
        AuthConstants.LOGIN_WITH_TOKEN,
        timestamp,
        () => {
            NotificationService.addNotification({
                message: 'fe.login.error',
                type: NOTIFICATION_TYPE_ERROR,
                timestamp: Date.now(),
            });
            window.location.replace(SIGN_IN_PATH);
        },
    );
}

export interface ILoginWithOAuthAction extends IAction {
    type: string;
    payload: {
        code: string;
        codeVerifier: string;
        redirectUri: string;
        provider: 'google' | 'microsoft';
    };
}

export function* loginWithOAuth(
    action: ILoginWithOAuthAction,
    timestamp?: number,
) {
    yield baseLogin(
        UserService.authUserWithOAuth,
        action.payload,
        AuthConstants.LOGIN_WITH_OAUTH,
        timestamp,
    );
}

export function* baseLogin(
    fn: any,
    payload: any,
    actionName: string,
    timestamp?: number,
    onError?: (error) => void,
) {
    const defaultErrorMsg = 'fe.login.error';

    try {
        const response = yield call(fn, payload);
        const tokens: IAuthTokens[] = response?.data;

        if (!tokens || tokens?.length === 0) {
            throw new Error(defaultErrorMsg);
        }

        if (tokens?.length === 1) {
            const { accessToken, refreshToken } = tokens[0];
            yield put(AuthActions.selectCustomer(accessToken, refreshToken));
        }

        yield put({ type: `${actionName}_SUCCESS`, payload: tokens });

        yield put(DemographicActions.toggleDemographics(true));
    } catch (ex) {
        const { message, response } = ex as {
            message?: string;
            response?: { status?: number };
        };
        let error = message;

        if (onError) {
            onError(error);
            return;
        }

        if (response?.status === 401) {
            error = defaultErrorMsg;
        } else {
            yield put(
                NotificationActions.error(error ?? defaultErrorMsg, timestamp),
            );
        }

        yield put({ type: `${actionName}_FAIL`, error });
    }
}

export const authSaga = [
    takeLatest(LOGIN, login),
    takeLatest(SELECT_CUSTOMER, selectCustomer),
    takeEvery(LOGOUT, logout),
    takeEvery(REFRESH_ACCESS, refreshAccess),
    takeLatest(GET_ACTIVE_QUESTIONNAIRES, getActiveQuestionnaires),
    takeLatest(AuthConstants.RESET_PASSWORD, resetPassword),
    takeLatest(AuthConstants.REQUEST_RESET_PASSWORD, requestResetPassword),
    takeLatest(AuthConstants.CHANGE_PASSWORD, changePassword),
    takeLatest(AuthConstants.LOGIN_WITH_TOKEN, loginWithToken),
    takeLatest(AuthConstants.LOGIN_WITH_OAUTH, loginWithOAuth),
];
